import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import axios from "axios";
import "./Question.css";

const Question = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Handle both arrays (memoized to avoid changing reference on every render)
  const allQuestions = useMemo(
    () => location.state?.questions || [],
    [location.state?.questions]
  );
  const wrongQuestions = useMemo(
    () => location.state?.wrongQuestions || [],
    [location.state?.wrongQuestions]
  );

  const [displayQuestions, setDisplayQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);

  const [questionType, setQuestionType] = useState("all"); // 'all' | 'wrong'
  const [numPerSet, setNumPerSet] = useState(null);
  const [rangeOptions, setRangeOptions] = useState([]);
  const [progressIndex, setProgressIndex] = useState(0);
  const [selectedRange, setSelectedRange] = useState(null);
  const [setupDone, setSetupDone] = useState(false);
  const [questionDone, setQuestionDone] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const lang = localStorage.getItem("lang") || "en";

  // ✅ Redirect if no data
  useEffect(() => {
    if (allQuestions.length === 0 && wrongQuestions.length === 0) {
      alert("No questions available now");
      navigate("/practice");
    }
  }, [allQuestions, wrongQuestions, navigate]);

  // ✅ Handle question type selection
  const handleTypeSelect = (type) => {
    setQuestionType(type);
    if (type === "all") {
      setDisplayQuestions(allQuestions);
    } else if (type === "wrong") setDisplayQuestions(wrongQuestions);

    // Reset further selections
    setNumPerSet(null);
    setRangeOptions([]);
    setSelectedRange(null);
    setSetupDone(false);
  };

  // ✅ When user selects number of questions
  const handleNumSelect = (num) => {
    if (num === "all") {
      setQuestions(displayQuestions);
      setSelectedRange({ start: 1, end: displayQuestions.length });
      setProgressIndex(1);
      setSetupDone(true);
      setQuestionDone(true);
      return;
    }

    setNumPerSet(num);
    const total = displayQuestions.length;
    const newRanges = [];

    for (let i = 1; i <= total; i += num) {
      const end = Math.min(i + num - 1, total);
      newRanges.push({ start: i, end });
    }

    setRangeOptions(newRanges);
  };

  // ✅ When user selects a range
  const handleRangeSelect = (range) => {
    const { start, end } = range;
    const selected = displayQuestions.slice(start - 1, end);
    setQuestions(selected);
    setSelectedRange(range);
    console.log("Range selected:", range);
    setProgressIndex(range.start);
    setSetupDone(true);
    setQuestionDone(true);
  };

  const currentQuestion = questions[currentIndex];

  const handleOptionClick = async (optionIndex) => {
    if (answered) return;

    const isCorrect = optionIndex === currentQuestion.correct_answer;
    setSelectedOption(optionIndex);
    setAnswered(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/commit_answer`,
        {
          question_id: currentQuestion.question_id,
          user_answer: optionIndex,
          is_correct: isCorrect,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
          },
        }
      );
    } catch (error) {
      console.error("Failed to commit answer", error);
    }
  };

  const goToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setAnswered(false);
      setProgressIndex(progressIndex + 1);
    } else {
      // ✅ All questions answered — go to PracticeResult.js
      navigate("/practice-result", {
        state: { total: questions.length },
      });
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgressIndex(progressIndex - 1);
      setSelectedOption(null);
      setAnswered(false);
    }
  };

  const getOptionClass = (index) => {
    if (!answered) return "option";
    if (index === currentQuestion.correct_answer) return "option correct";
    if (index === selectedOption) return "option incorrect";
    return "option";
  };

  // 🧭 Before showing questions: show setup selectors
  // 🧭 Before showing questions: setup selectors
  if (!setupDone) {
    return (
      <div className="overview">
        <Sidebar />
        <div className="question-wrapper">
          <div className="setup-container">
            <h2 style={{ fontWeight: 800 }}>Test Options</h2>
            <div className="type-selector">
              <button
                className={`num-btn ${questionType === "all" ? "active" : ""}`}
                onClick={() => handleTypeSelect("all")}
              >
                All Questions ({allQuestions.length})
              </button>
              <button
                className={`num-btn ${
                  questionType === "wrong" ? "active" : ""
                }`}
                onClick={() => handleTypeSelect("wrong")}
              >
                Previously Missed Questions ({wrongQuestions.length})
              </button>
            </div>

            {displayQuestions.length > 0 && (
              <>
                <h2>Select Number of Questions</h2>
                <div className="num-selector">
                  {[10, 20, 30].map((num) => (
                    <button
                      key={num}
                      className={`num-btn ${numPerSet === num ? "active" : ""}`}
                      onClick={() => handleNumSelect(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="num-btn all-btn"
                    onClick={() => handleNumSelect("all")}
                  >
                    All ({displayQuestions.length})
                  </button>
                </div>

                {rangeOptions.length > 0 && (
                  <>
                    <div className="range-title">Select Range</div>
                    <div className="range-selector">
                      {rangeOptions.map((range, index) => (
                        <button
                          key={index}
                          className="range-btn"
                          onClick={() => handleRangeSelect(range)}
                        >
                          {range.start} – {range.end}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 🧭 After setup done: show the quiz
  if (questionDone) {
    return (
      <div className="overview">
        <Sidebar />
        <div className="question-wrapper">
          <div className="question-content">
            <div className="progress-number">
              {progressIndex} / {selectedRange.end}
            </div>
            <div className="image-column">
              {currentQuestion.image &&
                (currentQuestion.image.match(/\.(mp4|webm|ogg)$/i) ? (
                  <video
                    src={currentQuestion.image}
                    className="question-image"
                    controls
                    autoPlay={true}
                    style={{ maxWidth: "30%" }}
                  />
                ) : (
                  <img
                    src={currentQuestion.image}
                    alt="question"
                    className="question-image"
                  />
                ))}
              <h2 className="question-text">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <span>{currentQuestion.question_text}</span>
                  <button
                    className="explanation-toggle"
                    onClick={() => setShowExplanation((prev) => !prev)}
                    title="Show Explanation"
                  >
                    ?
                  </button>
                </div>
                <div style={{ height: "20px" }}></div>
                {lang === "bn" && (
                  <span
                    style={{ fontFamily: "var(--ff-bangla)", display: "block" }}
                  >
                    {currentQuestion.question_text_bangla}
                  </span>
                )}
              </h2>
            </div>

            <div className="text-column">
              <div className="options-grid">
                {[0, 1, 2, 3].map((index) => {
                  const englishOption = currentQuestion[`option_${index + 1}`];
                  const banglaOption =
                    currentQuestion[`option_${index + 1}_bangla`];

                  return (
                    <button
                      key={index}
                      className={getOptionClass(index)}
                      onClick={() => handleOptionClick(index)}
                      disabled={answered}
                    >
                      <>
                        {englishOption}
                        {lang === "bn" && (
                          <span
                            style={{
                              fontFamily: "var(--ff-bangla)",
                              display: "block",
                            }}
                          >
                            {banglaOption}
                          </span>
                        )}
                      </>
                    </button>
                  );
                })}
              </div>

              {(answered || showExplanation) && (
                <div className="explanation-box">
                  <strong>{lang === "bn" ? "" : "Explanation:"}</strong>{" "}
                  <>
                    {currentQuestion.explanation}
                    {lang === "bn" && (
                      <span
                        style={{
                          fontFamily: "var(--ff-bangla)",
                          display: "block",
                        }}
                      >
                        {currentQuestion.explanation_bangla}
                      </span>
                    )}
                  </>
                </div>
              )}
            </div>
          </div>

          <div className="nav-buttons">
            <button onClick={goToPrevious} disabled={currentIndex === 0}>
              Previous
              {lang === "bn" && (
                <span
                  style={{ fontFamily: "var(--ff-bangla)", display: "block" }}
                >
                  পেছনে
                </span>
              )}
            </button>
            {currentIndex === questions.length - 1 ? (
              <button
                onClick={goToNext}
                /* disabled={currentIndex === questions.length - 1} */
              >
                See Result
                {lang === "bn" && (
                  <span
                    style={{ fontFamily: "var(--ff-bangla)", display: "block" }}
                  >
                    রেজাল্ট দেখুন
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={goToNext}
                /* disabled={currentIndex === questions.length - 1} */
              >
                Next
                {lang === "bn" && (
                  <span
                    style={{ fontFamily: "var(--ff-bangla)", display: "block" }}
                  >
                    পরবর্তী
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default Question;
